import React, { useState, useEffect } from 'react';

const SuperAdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    try {
      // Mock data - in real app, this would come from your admin API
      setStats({
        totalTenants: 42,
        activeTenants: 38,
        trialTenants: 15,
        paidTenants: 23,
        monthlyRevenue: 2847.50,
        totalRevenue: 18923.75,
        churnRate: 3.2,
        averageRevenuePerUser: 73.25
      });

      setTenants([
        {
          id: '1',
          name: 'Chris',
          slug: 'chris',
          contactEmail: 'cmbarrick@gmail.com',
          status: 'Trial',
          createdAt: new Date('2025-01-15'),
          trialEndsAt: new Date('2025-01-29'),
          userCount: 1,
          transactionCount: 0,
          plan: 'Starter',
          mrr: 0
        },
        {
          id: '2',
          name: 'Acme Corporation',
          slug: 'acme-corp',
          contactEmail: 'admin@acme.com',
          status: 'Active',
          createdAt: new Date('2024-12-01'),
          trialEndsAt: null,
          userCount: 8,
          transactionCount: 145,
          plan: 'Professional',
          mrr: 49
        },
        {
          id: '3',
          name: 'TechStart Inc',
          slug: 'techstart',
          contactEmail: 'billing@techstart.com',
          status: 'Active',
          createdAt: new Date('2024-11-15'),
          trialEndsAt: null,
          userCount: 15,
          transactionCount: 387,
          plan: 'Enterprise',
          mrr: 149
        },
        {
          id: '4',
          name: 'Beta Corp',
          slug: 'beta-corp',
          contactEmail: 'test@beta.com',
          status: 'Cancelled',
          createdAt: new Date('2024-10-20'),
          trialEndsAt: null,
          userCount: 3,
          transactionCount: 23,
          plan: 'Professional',
          mrr: 0
        }
      ]);

      setSubscriptions([
        {
          id: 'sub_1',
          tenantId: '2',
          tenantName: 'Acme Corporation',
          plan: 'Professional',
          status: 'Active',
          amount: 49,
          billingCycle: 'Monthly',
          nextBilling: new Date('2025-02-01'),
          stripeSubscriptionId: 'sub_1234567890',
          stripeCustomerId: 'cus_1234567890'
        },
        {
          id: 'sub_2',
          tenantId: '3',
          tenantName: 'TechStart Inc',
          plan: 'Enterprise',
          status: 'Active',
          amount: 149,
          billingCycle: 'Monthly',
          nextBilling: new Date('2025-02-15'),
          stripeSubscriptionId: 'sub_0987654321',
          stripeCustomerId: 'cus_0987654321'
        }
      ]);

    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeTenant = (tenantId) => {
    alert(`Upgrading tenant ${tenantId} - This would integrate with Stripe API`);
  };

  const handleSuspendTenant = (tenantId) => {
    if (confirm('Are you sure you want to suspend this tenant?')) {
      setTenants(tenants.map(t => 
        t.id === tenantId ? { ...t, status: 'Suspended' } : t
      ));
      alert('Tenant suspended successfully');
    }
  };

  const handleRefundSubscription = (subscriptionId) => {
    if (confirm('Are you sure you want to process a refund?')) {
      alert(`Processing refund for subscription ${subscriptionId} via Stripe API`);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Trial':
        return 'bg-blue-100 text-blue-800';
      case 'Suspended':
        return 'bg-red-100 text-red-800';
      case 'Cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: '📊' },
    { id: 'tenants', name: 'Tenants', icon: '🏢' },
    { id: 'subscriptions', name: 'Subscriptions', icon: '💳' },
    { id: 'revenue', name: 'Revenue', icon: '💰' },
    { id: 'settings', name: 'Settings', icon: '⚙️' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <div className="text-2xl font-bold text-indigo-600">
                👑 XeroPay Admin
              </div>
              <div className="ml-4 text-sm text-gray-500">
                Super Admin Portal
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-700">
                <div className="font-medium">Application Owner</div>
                <div className="text-xs text-gray-500">chris@xeropay.com</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
              >
                <span>{tab.icon}</span>
                <span>{tab.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="text-3xl">🏢</div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Total Tenants
                        </dt>
                        <dd className="text-3xl font-bold text-gray-900">
                          {stats.totalTenants}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="text-3xl">💰</div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Monthly Revenue
                        </dt>
                        <dd className="text-3xl font-bold text-gray-900">
                          {formatCurrency(stats.monthlyRevenue)}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="text-3xl">✅</div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Active Tenants
                        </dt>
                        <dd className="text-3xl font-bold text-gray-900">
                          {stats.activeTenants}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="text-3xl">📈</div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          ARPU
                        </dt>
                        <dd className="text-3xl font-bold text-gray-900">
                          {formatCurrency(stats.averageRevenuePerUser)}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Recent Activity
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="text-green-500">✅</div>
                    <div className="text-sm">
                      <span className="font-medium">Acme Corporation</span> upgraded to Professional plan
                    </div>
                    <div className="text-xs text-gray-500">2 hours ago</div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="text-blue-500">🆕</div>
                    <div className="text-sm">
                      <span className="font-medium">Chris</span> started a free trial
                    </div>
                    <div className="text-xs text-gray-500">4 hours ago</div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="text-red-500">❌</div>
                    <div className="text-sm">
                      <span className="font-medium">Beta Corp</span> cancelled their subscription
                    </div>
                    <div className="text-xs text-gray-500">1 day ago</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'tenants' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                All Tenants
              </h3>
              
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Organization
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Plan
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Users
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        MRR
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tenants.map((tenant) => (
                      <tr key={tenant.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {tenant.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {tenant.contactEmail}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(tenant.status)}`}>
                            {tenant.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {tenant.plan}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {tenant.userCount}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(tenant.mrr)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(tenant.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            {tenant.status === 'Trial' && (
                              <button
                                onClick={() => handleUpgradeTenant(tenant.id)}
                                className="text-indigo-600 hover:text-indigo-900"
                              >
                                Upgrade
                              </button>
                            )}
                            {tenant.status === 'Active' && (
                              <button
                                onClick={() => handleSuspendTenant(tenant.id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Suspend
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'subscriptions' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Active Subscriptions
                </h3>
                <a
                  href="https://dashboard.stripe.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-purple-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-purple-700 flex items-center space-x-2"
                >
                  <span>💳</span>
                  <span>Open Stripe Dashboard</span>
                </a>
              </div>
              
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tenant
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Plan
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Next Billing
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Stripe ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {subscriptions.map((sub) => (
                      <tr key={sub.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {sub.tenantName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {sub.plan}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(sub.amount)}/{sub.billingCycle.toLowerCase()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(sub.status)}`}>
                            {sub.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(sub.nextBilling)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-500">
                          {sub.stripeSubscriptionId}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <a
                              href={`https://dashboard.stripe.com/subscriptions/${sub.stripeSubscriptionId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-purple-600 hover:text-purple-900"
                            >
                              View in Stripe
                            </a>
                            <button
                              onClick={() => handleRefundSubscription(sub.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Refund
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'revenue' && (
          <div className="space-y-6">
            {/* Revenue Summary */}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="text-3xl">📊</div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Total Revenue
                        </dt>
                        <dd className="text-3xl font-bold text-gray-900">
                          {formatCurrency(stats.totalRevenue)}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="text-3xl">📈</div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Monthly Growth
                        </dt>
                        <dd className="text-3xl font-bold text-green-600">
                          +12.5%
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="text-3xl">⚠️</div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Churn Rate
                        </dt>
                        <dd className="text-3xl font-bold text-red-600">
                          {stats.churnRate}%
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Stripe Integration */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Stripe Integration
                </h3>
                
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <a
                    href="https://dashboard.stripe.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="text-2xl">💳</div>
                      <div>
                        <div className="font-medium">Stripe Dashboard</div>
                        <div className="text-sm text-gray-500">Manage payments and subscriptions</div>
                      </div>
                    </div>
                  </a>

                  <a
                    href="https://dashboard.stripe.com/analytics"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="text-2xl">📊</div>
                      <div>
                        <div className="font-medium">Revenue Analytics</div>
                        <div className="text-sm text-gray-500">Detailed revenue insights</div>
                      </div>
                    </div>
                  </a>

                  <a
                    href="https://dashboard.stripe.com/customers"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="text-2xl">👥</div>
                      <div>
                        <div className="font-medium">Customer Management</div>
                        <div className="text-sm text-gray-500">View and manage customers</div>
                      </div>
                    </div>
                  </a>

                  <a
                    href="https://dashboard.stripe.com/settings"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="text-2xl">⚙️</div>
                      <div>
                        <div className="font-medium">Stripe Settings</div>
                        <div className="text-sm text-gray-500">Configure payment settings</div>
                      </div>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6">
                Application Settings
              </h3>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Application Name
                  </label>
                  <input
                    type="text"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    defaultValue="XeroPay SaaS"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Support Email
                  </label>
                  <input
                    type="email"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    defaultValue="support@xeropay.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Trial Period (days)
                  </label>
                  <input
                    type="number"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    defaultValue="14"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Stripe Publishable Key
                  </label>
                  <input
                    type="text"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm font-mono"
                    placeholder="pk_live_..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Stripe Secret Key
                  </label>
                  <input
                    type="password"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm font-mono"
                    placeholder="sk_live_..."
                  />
                </div>

                <div className="pt-4">
                  <button
                    type="button"
                    className="bg-indigo-600 text-white px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium hover:bg-indigo-700"
                  >
                    Save Settings
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SuperAdminDashboard;
